const DAY_MS = 86_400_000;

export type DueStatus = "overdue" | "due-soon" | null;

/**
 * Local calendar midnight of an ISO date/timestamp. ponytail: the BE stores date-only fields as UTC
 * midnight, which maps to the same calendar day everywhere east of UTC (the team's zones); west of UTC
 * it lands one day early. Parse as a calendar date if that ever matters.
 */
export const localMidnight = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Whole calendar days from today to `iso` (negative = past). `null` when the date is missing/invalid. */
export const daysUntil = (iso?: string | null, now = new Date()): number | null => {
  const due = localMidnight(iso);
  if (!due) return null;
  const today = localMidnight(now.toISOString()) as Date;
  return Math.round((due.getTime() - today.getTime()) / DAY_MS);
};

/** Shared by objectives and KRs: only live items can be overdue; ≤7 days out is "due soon". */
export const dueStatusOf = (iso: string | null | undefined, isActive: boolean, now = new Date()): DueStatus => {
  if (!isActive) return null;
  const days = daysUntil(iso, now);
  if (days === null) return null;
  return days < 0 ? "overdue" : days <= 7 ? "due-soon" : null;
};

/** Whole calendar days since `iso` (0 = today). `null` when missing/invalid. */
export const daysSince = (iso?: string | null, now = new Date()): number | null => {
  const days = daysUntil(iso, now);
  return days === null ? null : Math.max(0, -days);
};

/** A KR whose number has not moved in two weeks makes the objective rollup stale. */
export const STALE_AFTER_DAYS = 14;
